// src/components/shops/postTemplates/Polaroid.tsx
//
// Prints laid out on a table, each tilted a little, with the caption written
// under the last one.
//
// The least corporate of the set and the only one that gets better with more
// photos rather than worse — four half-decent snapshots from behind the counter
// look like a good afternoon here, where in a neat grid they look like filler.

import { cachedUrl } from '../../../lib/storageUrl'
import SocialBrewBadge from '../SocialBrewBadge'
import { type TemplateProps } from './types'

// Fixed tilts rather than random ones: a template that renders differently on
// every pass is impossible to preview, and the export would never match what
// the shop approved.
const TILT = [-3.2, 2.4, -1.6, 3.0]

export default function Polaroid({
  title, body, photos, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  const prints = photos.slice(0, 4)
  const cols = prints.length === 1 ? 1 : 2

  return (
    <div
      style={{
        // A shade deeper than the app's cream so the prints read as sitting on
        // a table, and so the card's own edge survives on the export ground.
        background: '#e9ddc8',
        borderRadius: px(4),
        padding: `${px(18)} ${px(16)} ${px(14)}`,
      }}
      data-shop={shopName}
    >
      {prints.length > 0 && (
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: px(12),
            marginBottom: px(16),
            // The tilts push prints towards each other, so the block needs a
            // little room on both sides to avoid clipping at the card edge.
            padding: `${px(4)} ${px(6)}`,
          }}
        >
          {prints.map((url, i) => (
            <div
              key={i}
              style={{
                background: '#fffdf8',
                padding: `${px(7)} ${px(7)} ${px(16)}`,
                // Shadows are dropped from the image export, so the print also
                // carries a hairline — otherwise the white border would vanish
                // into the cream ground there.
                border: `${px(1)} solid rgba(36,24,16,0.08)`,
                boxShadow: `0 ${px(2)} ${px(6)} rgba(36,24,16,0.16)`,
                transform: `rotate(${TILT[i % TILT.length]}deg)`,
              }}
            >
              <div style={{ aspectRatio: '1 / 1', overflow: 'hidden', background: '#e8d8c0' }}>
                <img src={cachedUrl(url)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            </div>
          ))}
        </div>
      )}

      <h3
        className="font-display"
        style={{
          fontSize: px(19),
          lineHeight: 1.2,
          color: '#241810',
          fontWeight: 700,
          margin: 0,
          textWrap: 'balance',
        }}
      >
        {title}
      </h3>
      {body && (
        <p style={{ fontSize: px(13), lineHeight: 1.62, color: '#6e5038', marginTop: px(6) }}>
          {body}
        </p>
      )}

      <div style={{ marginTop: px(16) }}>
        <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="light" scale={scale} />
      </div>
    </div>
  )
}
